'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { TypeForm, TypeFormField, TypeFormFieldSkeleton } from '@fm-nx/contentful-types';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import * as Yup from 'yup';
import * as Contentful from 'contentful';
import { m as motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { BUDGET_RANGES } from '@fm-nx/utils';

import FieldSelect from '../field-select/field-select';
import FieldTextarea from '../field-textarea/field-textarea';
import FocusError from '../focus-error/focus-error';
import TextMarkdown from '../text-markdown/text-markdown';
import FieldInput from '../field-input/field-input';
import { getMinProjectDescriptionLength } from '@fm-nx/utils';

interface ContactFormValues {
    firstName: string;
    lastName: string;
    email: string;
    organization: string;
    phone: string;
    budgetRange: string;
    projectDescription: string;
    uri?: string;
    title?: string;
    hutk?: string;
    website?: string; // Honeypot field
}

const validationSchema = Yup.object({
    firstName: Yup.string().required('What is your first name.'),
    lastName: Yup.string().required('What is your last name.'),
    email: Yup.string().email('Give us a valid email.').required('Let us know your email.'),
    organization: Yup.string().required('Which company are you affiliated with?'),
    phone: Yup.string().required(`We'd like to give you a call.`),
    budgetRange: Yup.string().required('What is your budget range?'),
    projectDescription: Yup.string()
        .required('We want to know more!')
        .test('min-length-dynamic', function (value) {
            const { email } = this.parent;
            if (!email || !value) return true; // Let required validation handle empty values

            const minLength = getMinProjectDescriptionLength(email);
            if (value.length < minLength) {
                return this.createError({
                    message: `Please provide at least ${minLength} characters for your project description.`,
                });
            }
            return true;
        }),
});

const encode = (data: { [x: string]: string | number | boolean }) =>
    Object.keys(data)
        .map((key: string) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
        .join('&');

type GroupsType =
    | Record<string, never>
    | {
          [key: string]: TypeFormField<'WITHOUT_UNRESOLVABLE_LINKS'>;
      };

const groupByValue = (
    formFields: (Contentful.Entry<TypeFormFieldSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[]
): GroupsType => {
    const groups: GroupsType = {};
    for (const [, value] of Object.entries(formFields)) {
        const newKey = value?.fields.value;
        if (newKey) {
            groups[newKey] = value;
        }
    }
    return groups;
};

export const FormContact = ({ form }: { form: TypeForm<'WITHOUT_UNRESOLVABLE_LINKS'> }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: form.sys.id });
    const { fields } = form;
    const { fields: formFields, formErrorMessage, successMessage, submitButtonText, id, intro } = fields;

    const fieldsByValue = groupByValue(formFields);

    const [hasSuccess] = useState(false);
    const [hasSubmitError, setHasSubmitError] = useState(false);
    const router = useRouter();

    const initialValues: ContactFormValues = {
        firstName: '',
        lastName: '',
        email: '',
        organization: '',
        phone: '',
        budgetRange: '',
        projectDescription: '',
        website: '', // Honeypot field - should remain empty
    };

    const handleSubmit = async (values: ContactFormValues, formikHelpers: FormikHelpers<ContactFormValues>) => {
        const { setSubmitting, setFieldValue } = formikHelpers;

        // Honeypot check - if filled, silently reject (likely a bot)
        if (values.website) {
            setSubmitting(false);
            return;
        }

        // Normalize email to prevent duplicates across all systems (Heap, HubSpot)
        values.email = values.email?.trim()?.toLowerCase() || '';

        values.uri = window.location.hostname + window.location.pathname;
        values.title = window.document.title;

        if (window.document.cookie.split(';').filter((cookie) => cookie.trim().startsWith('hubspotutk')).length > 0) {
            values.hutk = window.document.cookie
                .split(';')
                .filter((cookie) => cookie.trim().startsWith('hubspotutk'))[0]
                .split('=')[1];
        }

        const body = encode({ 'form-name': 'contact', ...values });

        const handleSubmitError = () => {
            setHasSubmitError(true);
        };

        try {
            const response = await fetch('/.netlify/functions/hubspot-deal-creation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                credentials: 'same-origin',
                body,
            });

            if (response.status === 200) {
                // Identify user in Heap to link anonymous activity with their identity
                // Email is already normalized (trimmed/lowercased) and validated
                if (typeof window !== 'undefined' && window.heap && values.email) {
                    window.heap.identify(values.email);
                }
                router.push('/contact/thank-you');
            } else {
                handleSubmitError();
                setHasSubmitError(true);
                setSubmitting(false);
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
        } catch {
            handleSubmitError();
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({ isSubmitting, setFieldValue, errors, touched }) => (
                <Form noValidate id={id} method="POST">
                    <input type="hidden" name="form-name" value={id} />
                    {/* Honeypot field - hidden from users but visible to bots */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            left: '-9999px',
                            width: '1px',
                            height: '1px',
                            opacity: 0,
                            pointerEvents: 'none',
                        }}
                    >
                        <FieldInput
                            label="Website"
                            name="website"
                            type="text"
                            placeholder=""
                            autoComplete="off"
                            tabIndex={-1}
                        />
                    </div>
                    {intro && !hasSuccess && (
                        <div className="mb-6" {...inspectorProps({ fieldId: 'intro' })}>
                            <TextMarkdown text={intro} />
                        </div>
                    )}
                    {hasSubmitError && (
                        <div className=" bg-poppy">
                            <div
                                className="px-4 py-4 text-white! p-xl"
                                {...inspectorProps({ fieldId: 'formErrorMessage' })}
                            >
                                <TextMarkdown text={formErrorMessage || ''} mode="dark" />
                            </div>
                        </div>
                    )}
                    {hasSuccess && (
                        <div className="mb-20 bg-gray-900">
                            <p
                                className="px-4 py-6 p-xl text-green-400"
                                {...inspectorProps({ fieldId: 'successMessage' })}
                            >
                                {successMessage}
                            </p>
                        </div>
                    )}
                    {!hasSuccess && (
                        <div>
                            <FocusError />
                            <FieldInput
                                label={fieldsByValue['firstName'].fields.label}
                                name="firstName"
                                type="text"
                                placeholder={fieldsByValue['firstName'].fields.placeholder}
                                required
                            />
                            <FieldInput
                                label={fieldsByValue['lastName'].fields.label}
                                name="lastName"
                                type="text"
                                placeholder={fieldsByValue['lastName'].fields.placeholder}
                                required
                            />
                            <FieldInput
                                label={fieldsByValue['email'].fields.label}
                                name="email"
                                type="email"
                                placeholder={fieldsByValue['email'].fields.placeholder}
                                required
                            />
                            <FieldInput
                                label={fieldsByValue['organization'].fields.label}
                                name="organization"
                                type="text"
                                placeholder={fieldsByValue['organization'].fields.placeholder}
                                required
                            />
                            <FieldInput
                                label={fieldsByValue['phone'].fields.label}
                                name="phone"
                                type="text"
                                placeholder={fieldsByValue['phone'].fields.placeholder}
                                required
                            />
                            <FieldSelect label={fieldsByValue['budgetRange'].fields.label} name="budgetRange" required>
                                <option value="" disabled>
                                    - Choose one -
                                </option>
                                {BUDGET_RANGES.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </FieldSelect>
                            <FieldTextarea
                                label={fieldsByValue['projectDescription'].fields.label}
                                name="projectDescription"
                                placeholder={fieldsByValue['projectDescription'].fields.placeholder}
                                required
                            />
                            <div className="flex justify-center my-10">
                                <motion.button
                                    type="submit"
                                    className="btn gtm-contact-form__submit"
                                    disabled={isSubmitting}
                                >
                                    <span {...inspectorProps({ fieldId: 'submitButtonText' })}>
                                        {isSubmitting ? 'Hang on' : submitButtonText}
                                    </span>
                                </motion.button>
                            </div>
                        </div>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default FormContact;
